"use client";

import React, { useRef } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CircleCheck as CheckCircle2, Zap, ShieldCheck, Clock } from 'lucide-react';
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
gsap.registerPlugin(useGSAP);
export default function HeroElectrical() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const tl = gsap.timeline({
      defaults: {
        ease: "power3.out",
        duration: 0.8
      }
    });
    tl.from(".hero-badge", {
      opacity: 0,
      y: 20
    }).from(".hero-title", {
      opacity: 0,
      y: 20
    }, "-=0.6").from(".hero-desc", {
      opacity: 0,
      y: 20
    }, "-=0.6").from(".hero-cta", {
      opacity: 0,
      y: 20
    }, "-=0.6").from(".hero-trust > div", {
      opacity: 0,
      y: 10,
      stagger: 0.1
    }, "-=0.4").from(".hero-image", {
      opacity: 0,
      scale: 0.95,
      duration: 1
    }, "-=1");
  }, {
    scope: container
  });
  return <section id="heroelectricalservices" ref={container} className="relative overflow-hidden bg-background py-16 sm:py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
          {}
          <div className="max-w-2xl">
            <Badge variant="secondary" className="hero-badge mb-6 flex w-fit items-center gap-2 px-3 py-1.5 text-sm">
              <Zap className="size-4 text-primary" />
              <span>Independent Electrical Pros</span>
            </Badge>

            <h2 className="hero-title font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Atlanta Electrical Services — Safe, Dependable Solutions
            </h2>

            <p className="hero-desc mt-6 text-lg leading-8 text-muted-foreground">
              Connect with experienced local pros for dependable <Link to="/ElectricalServicesPage" className="text-primary underline hover:text-primary/80 font-medium">electrical repairs</Link> and installations for your home.
            </p>

            <p className="mt-4 text-base leading-7 text-muted-foreground">
              Find qualified independent professionals providing code-compliant workmanship. Explore our comprehensive <Link to="/HomeMaintenancePage" className="text-primary underline hover:text-primary/80 font-medium">home maintenance</Link> solutions for your property.
            </p>

            <div className="hero-cta mt-8 flex flex-wrap items-center gap-4">
              <Link to="#bookingrequest">
                <Button size="lg" className="h-12 px-8 text-base">
                  Request a Quote
                </Button>
              </Link>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Clock className="size-4" />
                </span>
                Ask About Availability
              </div>
            </div>

            {}
            <div className="hero-trust mt-10 flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:gap-8">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-5 text-primary" />
                <span className="text-sm font-medium text-foreground">
                  Network Onboarding
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-5 text-primary" />
                <span className="text-sm font-medium text-foreground">
                  Quote Before Work Starts
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-5 text-primary" />
                <span className="text-sm font-medium text-foreground">
                  Code Compliant
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="hero-image relative w-full max-w-xl lg:ml-auto lg:max-w-none">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-2xl sm:aspect-[3/2] lg:aspect-[4/5]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrician_working_panel_atlanta.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrician_working_panel_atlanta.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrician_working_panel_atlanta.png-wvc-sizes" alt="Electrical pro working on a residential electrical panel" className="absolute inset-0 h-full w-full object-cover" />
              {}
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-foreground/10"></div>
            </div>

            {}
            <div className="absolute -bottom-6 -left-6 flex items-center gap-4 rounded-xl border border-border bg-card p-4 shadow-xl sm:-left-8 sm:bottom-8">
              <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10">
                <ShieldCheck className="size-6 text-primary" />
              </div>
              <div>
                <p className="font-default text-sm font-bold text-foreground">
                  Safety First
                </p>
                <p className="text-xs text-muted-foreground">
                  Independent Pros
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}